// Same gender keys as the mobile waitlist (app/auth/result.tsx) and backend GENDERS.
export const GENDERS = [
  { key: "woman", label: "Woman" },
  { key: "man", label: "Man" },
  { key: "nonbinary", label: "Nonbinary" },
  { key: "other", label: "Other" },
  { key: "prefer_not_to_say", label: "Prefer not to say" },
];

export const SUPPORT_URL = "https://crushwink.com/support";

// Mirrors backend domain.py sanctioned rules (server is the source of truth; it rejects anything but Off).
export const SANCTIONED_COUNTRIES = { CU: "Cuba", IR: "Iran", KP: "North Korea", SY: "Syria" };
export const SANCTIONED_SUBREGIONS = ["Crimea", "Sevastopol", "Donetsk", "Luhansk"];
const SANCTIONED_NAMES = ["cuba", "iran", "north korea", "syria", "democratic people's republic of korea",
  "syrian arab republic", "islamic republic of iran"];
const SUB_WORDS = ["crimea", "sevastopol", "donetsk", "luhansk", "lugansk"];

export function isSanctioned({ type, values = [], country, state }) {
  const vals = values.map((v) => String(v).trim().toLowerCase());
  const isCountry = (c) => !!c && (Object.keys(SANCTIONED_COUNTRIES).includes(c.toUpperCase()) || SANCTIONED_NAMES.includes(c.toLowerCase()));
  if (type === "country") return vals.some(isCountry);
  if (isCountry(country || "")) return true;
  const text = [state, ...(type === "state" || type === "city" ? vals : [])].filter(Boolean).join(" ").toLowerCase();
  return SUB_WORDS.some((w) => text.includes(w));
}

export const REGION_TYPES = [
  { key: "zip", label: "Postal codes" },
  { key: "city", label: "City" },
  { key: "state", label: "State / province" },
  { key: "country", label: "Country" },
];

export const PRECEDENCE_HELP = "Most specific rule wins: postal code > city > state/province > country. Sanctioned countries are always blocked.";

export const REGION_STATUSES = [
  { key: "off", label: "Off" },
  { key: "waitlist", label: "Waitlist" },
  { key: "on", label: "On" },
];

export const TIERS = [
  { key: "crushr", label: "Crushr" },
  { key: "winkr", label: "Winkr" },
  { key: "duo", label: "Duo" },
];

// Settings render from GET /api/admin/settings, grouped by the API `group` field.
// SETTING_META gives friendly labels/units (and a fallback group when the API sends none);
// unknown keys fall back to the API description (or a humanized key) and a guessed group.
export const SETTINGS_GROUPS = [
  { title: "Allowances", hint: "Daily Crushes and Winks, how fast they refill, and Crush note length." },
  { title: "Super Crush", hint: "Welcome gift, weekly Super Crushes by tier, and note length." },
  { title: "Matching", hint: "Time to send the first message after a Wink, and what happens after a decline." },
  { title: "Discover", hint: "Who shows up in Discover, how far away, and how much newer profiles are favored." },
  { title: "Chat", hint: "Voice messages, when quiet chats wind down, gentle nudges, and the money/links warning." },
  { title: "Safety", hint: "Reports, auto-hide, bans and appeals, and selfie retakes." },
  { title: "Notifications", hint: "Keep notifications calm. Quiet hours use each person's local time." },
  { title: "Profile", hint: "Age limits, photo counts, and how long each profile field can be." },
  { title: "Account", hint: "Phone number changes and account deletion." },
  { title: "Store", hint: "How purchases are verified, and how long a lapsed subscription keeps its tier." },
  { title: "SMS", hint: "Who sends login and website codes, and how many a phone or network can ask for. A 30-second cooldown always applies." },
  { title: "Photo screening", hint: "Automatic photo checks and how long rejected uploads are kept for review." },
  { title: "Launch", hint: "Invite codes, referrals, the Founding Crush badge and the wave Launch grant." },
  { title: "Website", hint: "The public site: its base URL (the app opens /support, /privacy, /terms, /guidelines there), contact-form limits and web phone-check tokens." },
  { title: "Team & data", hint: "Support grant limits, data-export links and full-delete confirmation." },
  { title: "Other", hint: "Settings without a group yet." },
];

const M = (group, label, unit = "") => ({ group, label, unit });
export const SETTING_META = {
  crush_daily: M("Allowances", "Daily crushes", "per day"),
  wink_daily: M("Allowances", "Daily winks", "per day"),
  refill_hours_single_lane: M("Allowances", "Refill every (hours) — Crushr/Winkr lane", "hours"),
  refill_hours_duo: M("Allowances", "Refill every (hours) — Duo, both lanes", "hours"),
  crush_note_max: M("Allowances", "Crush note length (characters)", "chars"),
  super_crush_welcome: M("Super Crush", "Welcome Super Crushes at signup"),
  super_crush_weekly_free: M("Super Crush", "Weekly Super Crushes — Free", "per week"),
  super_crush_weekly_single_lane: M("Super Crush", "Weekly Super Crushes — Crushr and Winkr", "per week"),
  super_crush_weekly_duo: M("Super Crush", "Weekly Super Crushes — Duo", "per week"),
  super_crush_note_max: M("Super Crush", "Super Crush note length (characters)", "chars"),
  first_message_hours: M("Matching", "First message window (hours)", "hours"),
  first_message_hours_duo: M("Matching", "First message window for Duo (hours)", "hours"),
  decline_cooldown_days: M("Matching", "Decline cooldown (days)", "days"),
  active_window_days: M("Discover", "Show people active in the last", "days"),
  default_distance_miles: M("Discover", "Default search distance", "miles"),
  max_distance_miles: M("Discover", "Largest distance someone can choose", "miles"),
  feed_newest_bias_pct: M("Discover", "Boost for newer profiles", "%"),
  voice_max_seconds: M("Chat", "Voice message max (seconds)", "seconds"),
  chat_poll_fallback_seconds: M("Chat", "Check for new messages every (when live updates are down)", "seconds"),
  sms_provider: { ...M("SMS", "Text message provider"), warning: "test = fixed code, no texts sent. twilio = real texts via Twilio Verify. SMS_TEST_MODE=true on the server always forces test." },
  sms_per_phone_hour: M("SMS", "Codes per phone number per hour (app + website share it)", "per hour"),
  sms_per_ip_hour: M("SMS", "Codes per network (IP) per hour", "per hour"),
  nudity_threshold: { ...M("Photo screening", "Nudity threshold (0–1, lower = stricter)"), warning: "A photo is rejected when its nudity score is at or above this value. Lower it to reject more; raise it to reject less." },
  photo_require_person: M("Photo screening", "Reject photos with no visible person"),
  quarantine_retention_days: M("Photo screening", "Keep rejected uploads privately for", "days"),
  chat_idle_days: M("Chat", "Days quiet before Expiring", "days"),
  chat_unmatch_days: M("Chat", "More days before auto-unmatch", "days"),
  nudge_no_reply_hours: M("Chat", "No-reply nudge after (hours)", "hours"),
  nudge_quiet_days: M("Chat", "Quiet nudge after (days)", "days"),
  flagged_words: M("Chat", "Flagged words (money/links warning)"),
  auto_hide_reports: M("Safety", "Reporters needed to auto-hide a profile", "people"),
  auto_hide_window_days: M("Safety", "Window for counting those reports", "days"),
  report_review_hours: M("Safety", "Review auto-hidden profiles within", "hours"),
  report_note_max: M("Safety", "Report note length (characters)", "chars"),
  report_chat_messages: M("Safety", "Chat messages attached to a report", "messages"),
  report_dedupe_days: M("Safety", "Repeat reports merge within", "days"),
  ban_blocks_device: M("Safety", "Bans also block the device"),
  appeal_max_chars: M("Safety", "Appeal length (characters)", "chars"),
  ban_face_retention_days: M("Safety", "Keep banned face data for", "days"),
  min_age: M("Profile", "Minimum age (18 is the floor)", "years"),
  max_age: M("Profile", "Maximum age", "years"),
  photo_min: M("Profile", "Fewest photos to show in Discover", "photos"),
  photo_max: M("Profile", "Most photos on a profile", "photos"),
  bio_max: M("Profile", "Bio length (characters)", "chars"),
  prompt_answer_max: M("Profile", "Prompt answer length (characters)", "chars"),
  interests_max: M("Profile", "Most interests per profile", "interests"),
  languages_max: M("Profile", "Most languages per profile", "languages"),
  name_max: M("Profile", "First name length (characters)", "chars"),
  occupation_max: M("Profile", "Occupation length (characters)", "chars"),
  ethnicity_max: M("Profile", "Ethnicity length (characters)", "chars"),
  currently_in_max: M("Profile", "\u201cCurrently in\u201d length (characters)", "chars"),
  pronouns_custom_max: M("Profile", "Custom pronouns length (characters)", "chars"),
  height_min_cm: M("Profile", "Shortest height allowed", "cm"),
  height_max_cm: M("Profile", "Tallest height allowed", "cm"),
  chat_text_max: M("Chat", "Chat message length (characters)", "chars"),
  first_message_max: M("Chat", "First message length (characters)", "chars"),
  selfie_retakes_per_day: M("Safety", "Selfie retakes allowed per day", "per day"),
  quiet_start: M("Notifications", "Quiet hours start (hour, 0–23, their local time)", "hour"),
  quiet_end: M("Notifications", "Quiet hours end (hour, 0–23) — held pushes go out as one bundle", "hour"),
  push_daily_cap: M("Notifications", "Most pushes per person per day (safety and Super Crush don’t count)", "per day"),
  push_bundle_minutes: M("Notifications", "Same kind from the same person within this window = one push", "minutes"),
  push_reply_silence_hours: M("Notifications", "Push a reply only after this many quiet hours in a chat", "hours"),
  push_preview_chars: M("Notifications", "First-message preview length in the push (characters)", "chars"),
  push_onscreen_seconds: M("Notifications", "Skip the push if they viewed that screen within", "seconds"),
  store_mode: { ...M("Store", "Store mode"), warning: "Real requires RevenueCat keys and a native build. Mock purchases are always refused in production." },
  grace_days: M("Store", "Billing grace (days)", "days"),
  invite_code_days: M("Launch", "Wave invite codes stay valid for", "days"),
  referral_codes_per_user: M("Launch", "Referral codes per member (after onboarding)", "codes"),
  referral_reward_super_crushes: M("Launch", "Super Crushes each person gets for a referral", "per referral"),
  founding_limit: M("Launch", "Founding Crush badges given automatically", "people"),
  wave_free_duo_days: M("Launch", "Launch grant: days of Duo with a wave code", "days"),
  social_instagram_url: M("Website", "Instagram link in the site footer (empty hides it)"),
  social_facebook_url: M("Website", "Facebook link in the site footer (empty hides it)"),
  social_tiktok_url: M("Website", "TikTok link in the site footer (empty hides it)"),
  site_base_url: M("Website", "Website address (change to https://crushwink.com at launch)"),
  contact_max_chars: M("Website", "Longest contact-form message", "chars"),
  contact_per_ip_hour: M("Website", "Contact messages per visitor per hour", "per hour"),
  contact_per_email_hour: M("Website", "Contact messages per email address per hour", "per hour"),
  web_scope_token_minutes: M("Website", "Web appeal/delete phone check stays valid for", "minutes"),
  support_grant_max_days: M("Team & data", "Longest tier grant Support can give", "days"),
  export_link_minutes: M("Team & data", "Data-export download link works for", "minutes"),
  delete_confirm_minutes: M("Team & data", "Full-delete confirmation stays valid for", "minutes"),
  phone_change_cooldown_days: M("Account", "Days between phone number changes", "days"),
  account_deletion_grace_days: M("Account", "Days before a deleted account is purged", "days"),
};

function guessGroup(key) {
  if (/feed|distance|active_window/.test(key)) return "Discover";
  if (/super_crush/.test(key)) return "Super Crush";
  if (/selfie|verif|report|ban|hide/.test(key)) return "Safety";
  if (/push|quiet/.test(key)) return "Notifications";
  if (/_daily|daily_|refill|note_max/.test(key)) return "Allowances";
  if (/first_message|decline|match/.test(key)) return "Matching";
  if (/chat|unmatch|voice|nudge|flagged/.test(key)) return "Chat";
  if (/store|grace|billing/.test(key)) return "Store";
  if (/phone|account|deletion/.test(key)) return "Account";
  return "Other";
}

const UNIT_HINTS = [[/_pct$/, "%"], [/_miles$/, "miles"], [/_days$/, "days"], [/_hours$/, "hours"], [/_per_day$/, "per day"]];

export function settingMeta(s) {
  const known = SETTING_META[s.key];
  const group = (s.group && !(s.group === "Other" && known) ? s.group : null) || known?.group || guessGroup(s.key);
  if (known) return { ...known, group };
  const words = s.key.replace(/_/g, " ");
  const label = s.description || words.charAt(0).toUpperCase() + words.slice(1);
  return { group, label, unit: UNIT_HINTS.find(([re]) => re.test(s.key))?.[1] || "" };
}

export const CONTENT_DOCS = [
  { key: "terms", label: "Terms of Service", reaccept: true },
  { key: "privacy", label: "Privacy Policy", reaccept: true },
  { key: "safety", label: "Safety" },
  { key: "chat_safety_tips", label: "Chat safety tips" },
  { key: "warning_message", label: "Warning message" },
  {
    key: "push_copy", label: "Push notification copy", rows: 18, noPreview: true,
    help: "One “key: template” per line. Placeholders: [Name], [preview], [summary], [count]. Nouns use “one|many”. Lines you leave out use the built-in default.",
    rule: "No emoji or urgency words (like “hurry”, “last chance”, “expiring”). Saving will be rejected.",
  },
  { key: "face_consent", label: "Selfie check: consent", help: "Shown in the app before the automatic selfie check (only when it's turned on)." },
  { key: "face_consent_actions", label: "Selfie check: buttons", rows: 4, noPreview: true, help: "One “key: label” per line: agree, decline." },
  { key: "privacy_face_data", label: "Privacy: Face data", help: "Added to the Privacy Policy as a “Face data” section while the automatic selfie check is on. {setting_key} placeholders are filled in." },
  { key: "hard_no_copy", label: "Hard no warnings", rows: 12, noPreview: true, help: "One “key: text” per line. The warning a member sees when someone breaks one of their hard no's." },
  { key: "faq", label: "FAQ", help: "“## Group name” starts a group. Inside a group, separate Q&As with a blank line: first line is the question, the lines below are the answer. Blocks before any “## ” go to “General”." },
  { key: "eligibility_sentence", label: "Eligibility sentence" },
  { key: "not_in_area_message", label: "Not-in-area message" },
  { key: "support_url", label: "Support URL" },
  { key: "tagline", label: "Tagline", rows: 2, noPreview: true, help: "One short line. Shown in the website hero and footer; changes go live without a rebuild." },
  { key: "home_copy", label: "Website: Home", rows: 12, noPreview: true, help: "One “key: value” per line: headline, tagline, step_1_title, step_1_body, step_2_title, step_2_body, step_3_title, step_3_body, cta. {setting_key} placeholders work." },
  { key: "how_it_works_copy", label: "Website: How it works", rows: 16, help: "“## Heading” starts each section. Use {setting_key} placeholders for numbers, e.g. {crush_daily}, {first_message_hours}, {super_crush_weekly_duo}." },
  { key: "plans_copy", label: "Website: Plans", rows: 14, help: "One “## ” section per plan, headed exactly Free, Crushr, Winkr, Duo (the price cards match on these). Prices come from Pricing, never from this text." },
  { key: "safety_page", label: "Website: Safety", rows: 16, help: "“## Heading” sections. A section whose heading contains “tips” links to the Safety tips (chat_safety_tips) block." },
  { key: "support_copy", label: "Website: Support", rows: 14, help: "“## ” sections: the first is the intro; then sections whose headings contain “problem”, “appeal” and “delete” head those parts of /support." },
  { key: "community_guidelines", label: "Community guidelines", rows: 16, help: "Shown at /guidelines with “Last updated · Version”. “## Heading” sections; {min_age} works." },
  { key: "site_footer", label: "Website: Footer", rows: 6, noPreview: true, help: "One “key: value” per line: email, press_line. Social links are set in Settings → Website (social_*_url)." },
  { key: "photo_overturned", label: "Photo restored notice", rows: 5, noPreview: true, help: "Sent when a moderator overturns a rejected photo. Title is the notice title; body is the text." },
  { key: "wave_invite_email", label: "Wave invite email", rows: 10, noPreview: true, help: "Title is the email subject. Placeholders: [code], [days], [app_store_url], [play_store_url]." },
  { key: "app_links", label: "App links", rows: 6, noPreview: true, help: "One “key: url” per line: app_store, play_store, invite_url (use [code]), console_url." },
  { key: "referral_share", label: "Referral share text", rows: 5, noPreview: true, help: "What members share with a friend. Use [code] and [link] for their code and invite link." },
  { key: "verification_retake", label: "Verification retake notice", rows: 5, noPreview: true, help: "Sent when an admin uses “Resend verification”. Asks the person to take a new selfie." },
  { key: "team_invite_email", label: "Team invite email", rows: 8, noPreview: true, help: "Title is the email subject. Placeholders: [role], [email], [password], [console_url]." },
];

// Mirrors backend safety.py REPORT_REASONS / BAN_CATEGORIES.
export const REPORT_REASONS = {
  fake_profile: "Fake profile", inappropriate_photos: "Inappropriate photos", harassment: "Harassment or abuse",
  scam: "Scam or asking for money", underage: "Underage", other: "Other",
};
export const BAN_CATEGORIES = { ...REPORT_REASONS, other: "Breaking our community guidelines" };

// Admin Log "area" values (backend core.AREAS page names).
export const CONSOLE_AREAS = ["Dashboard", "Users", "Reports", "Appeals", "Verification", "Prompts", "Interests", "Launch", "Support inbox", "Rejected photos", "Eligibility",
  "Pricing", "Promotions", "Content", "Notifications", "Feature flags", "Settings", "Admin Log", "Team"];

// General Content format help (shown above every doc editor).
export const CONTENT_FORMAT_HELP = "Formats: “key: value” one per line; a line starting “## ” starts a section; {setting_key} placeholders (e.g. {crush_daily}, {first_message_hours}, {min_age}) are filled with live Settings.";
